import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import TomSelect from "tom-select"
import { allUsersStore } from "../stores/organization/all_users_store"
import { getCsrfToken } from "shared/form_helpers"

/**
 * ProjectSetupController
 *
 * Manages the project creation (onboarding) page.
 *
 * Data is NOT fetched server-side. The members multi-select is populated client-side from the
 * shared stores fed by the existing suppliers:
 *   - allLocalesStore  (suppliers--locales--all-locales-supplier)  → regions/buildings/areas
 *   - allUsersStore    (suppliers--organization--users-supplier)   → org users
 * Both suppliers paginate through every page, so large orgs are handled correctly.
 *
 * Facilities use the shared trees--facility-tree checkbox-tree widget (its own controller
 * instance on the tree wrapper), which reads allLocalesStore and dispatches
 * trees--facility-tree:selectionChanged with '!'-joined locale PATHS. This controller captures
 * those selections via onFacilitiesChanged. An "All Facilities" checkbox selects the '*'
 * wildcard and disables the tree.
 *
 * Facility values submitted to /api/projects are valid locale PATHS (or '*'), i.e. the
 * '!'-joined chain of ids:
 *   region:   region.id
 *   building: `${region.id}!${building.id}`
 *   area:     `${region.id}!${building.id}!${area.id}`
 *
 * Connects to data-controller="project-setup"
 */
export default class extends Controller {
    static stores = [
        allUsersStore
    ]

    static targets = [
        "form",
        "nameInput",
        "descriptionInput",
        "facilityTree",
        "allFacilitiesCheckbox",
        "membersSelect",
        "submitButton",
        "errorMessage",
        "successMessage"
    ]

    static values = {
        currentUserId: String
    }

    connect() {
        useStore(this)

        if (this.hasFormTarget) {
            this.formTarget.addEventListener('submit', (e) => this.createProject(e))
        }

        // Facilities are captured from the trees--facility-tree widget via onFacilitiesChanged.
        this._selectedFacilities = []

        // Initialize TomSelect on members multi-select
        if (this.hasMembersSelectTarget) {
            this.membersTomSelect = new TomSelect(this.membersSelectTarget, {
                plugins: ['remove_button'],
                placeholder: 'Select members to add',
                create: false,
                maxItems: null
            })
        }

        // Populate from whatever the store already holds (suppliers may have loaded before
        // this controller connected). Store update callbacks handle later async arrivals.
        this._renderMembers(this.allUsersStoreValue)
    }

    disconnect() {
        if (this.membersTomSelect) {
            this.membersTomSelect.destroy()
            this.membersTomSelect = null
        }
    }

    // --- Store callbacks (fire when suppliers finish/refresh their fetch) ---

    onAllUsersStoreUpdate(users) {
        this._renderMembers(users)
    }

    // --- Facility tree widget callbacks / actions ---

    /**
     * Capture the current facility selections emitted by the trees--facility-tree widget.
     * detail.selections is an Array of '!'-joined locale paths.
     */
    onFacilitiesChanged(event) {
        this._selectedFacilities = event.detail?.selections || []
    }

    /**
     * "All Facilities" toggle. When checked, facilities resolve to ['*'], which SUPERSEDES any
     * specific selections — so the tree's checkboxes are cleared (via its current-selections-value)
     * and the tree is disabled/dimmed. When unchecked, the tree is re-enabled starting empty (the
     * wildcard replaced the prior specific picks); the admin re-selects facilities.
     */
    toggleAllFacilities(event) {
        const checked = event.target.checked

        if (checked) {
            // Clear the tree's selections: setting current-selections-value drives the tree's
            // currentSelectionsValueChanged, which resets its checkboxes and re-renders.
            this._clearFacilityTree()
        }

        if (this.hasFacilityTreeTarget) {
            if (checked) {
                this.facilityTreeTarget.classList.add('disabled')
                this.facilityTreeTarget.style.opacity = '0.5'
                this.facilityTreeTarget.style.pointerEvents = 'none'
            } else {
                this.facilityTreeTarget.classList.remove('disabled')
                this.facilityTreeTarget.style.opacity = ''
                this.facilityTreeTarget.style.pointerEvents = ''
            }
        }
    }

    // Clear the facility tree's selections by resetting its Stimulus current-selections-value.
    // The tree controller's currentSelectionsValueChanged rebuilds its internal set and re-renders,
    // so all node checkboxes become unchecked. Also clears our captured selection.
    _clearFacilityTree() {
        this._selectedFacilities = []
        if (this.hasFacilityTreeTarget) {
            this.facilityTreeTarget.setAttribute(
              'data-trees--facility-tree-current-selections-value',
              '[]'
            )
        }
    }

    // --- Option builders ---

    /**
     * Rebuild the members TomSelect options from the users store, excluding the current
     * admin (auto-added as a member on create), preserving the current selection.
     */
    _renderMembers(usersValue) {
        if (!this.membersTomSelect) return

        const users = Array.isArray(usersValue) ? usersValue : []
        const currentUserId = this.hasCurrentUserIdValue ? this.currentUserIdValue : ''

        const selected = this.membersTomSelect.getValue()

        this.membersTomSelect.clearOptions()

        let order = 0
        users.forEach((user) => {
            if (!user || !user.id) return
            if (currentUserId && user.id === currentUserId) return
            const label = user.name ? `${user.name} (${user.email})` : user.email
            this.membersTomSelect.addOption({ value: user.id, text: label, $order: order++ })
        })

        this.membersTomSelect.refreshOptions(false)

        if (selected && selected.length) {
            this.membersTomSelect.setValue(selected, true)
        }
    }

    async createProject(event) {
        event.preventDefault()

        const name = this.hasNameInputTarget ? this.nameInputTarget.value.trim() : ''
        const description = this.hasDescriptionInputTarget ? this.descriptionInputTarget.value.trim() : ''

        if (!name) {
            this._showError('Project name is required.')
            return
        }

        if (name.length > 256) {
            this._showError('Project name must be 256 characters or less.')
            return
        }

        // Resolve facilities: '*' wildcard when "All Facilities" is checked, otherwise the
        // paths captured from the trees--facility-tree widget.
        const allFacilities = this.hasAllFacilitiesCheckboxTarget && this.allFacilitiesCheckboxTarget.checked
        const locales = allFacilities ? ['*'] : (this._selectedFacilities || [])

        if (!locales || locales.length === 0) {
            this._showError('At least one facility must be selected.')
            return
        }

        // Get selected user IDs from TomSelect
        const userIds = this.membersTomSelect ? this.membersTomSelect.getValue() : []

        this._disableSubmit()
        this._clearMessages()

        try {
            // Create the project
            const response = await fetch('/api/projects', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({ name, description, locales })
            })

            if (!response.ok) {
                const data = await response.json()
                const message = data?.message || data?.errors?.[0]?.message || 'Failed to create project.'
                this._showError(message)
                this._enableSubmit()
                return
            }

            const data = await response.json()
            const project = data?.data || data

            // Add selected users as members
            if (userIds.length > 0 && project.id) {
                await this._addMembers(project.id, userIds)
            }

            // Clear form and show success
            this._clearForm()
            this._showSuccess(`Project "${project.name}" created successfully.`)

            // Redirect to project selection after brief delay
            setTimeout(() => {
                window.location.href = '/projects/select'
            }, 1500)
        } catch (error) {
            console.error('Error creating project:', error)
            this._showError('A network error occurred. Please try again.')
        } finally {
            this._enableSubmit()
        }
    }

    async _addMembers(projectId, userIds) {
        const results = await Promise.allSettled(
            userIds.map(userId =>
                fetch(`/api/projects/${projectId}/members`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': getCsrfToken()
                    },
                    body: JSON.stringify({ user_id: userId })
                })
            )
        )

        const failures = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok))
        if (failures.length > 0) {
            console.warn(`Failed to add ${failures.length} member(s) to project.`)
        }
    }

    _clearForm() {
        if (this.hasNameInputTarget) this.nameInputTarget.value = ''
        if (this.hasDescriptionInputTarget) this.descriptionInputTarget.value = ''
        this._selectedFacilities = []
        if (this.hasAllFacilitiesCheckboxTarget) this.allFacilitiesCheckboxTarget.checked = false
        if (this.hasFacilityTreeTarget) {
            this.facilityTreeTarget.classList.remove('disabled')
            this.facilityTreeTarget.style.opacity = ''
            this.facilityTreeTarget.style.pointerEvents = ''
        }
        if (this.membersTomSelect) this.membersTomSelect.clear()
    }

    _showError(message) {
        if (this.hasErrorMessageTarget) {
            this.errorMessageTarget.textContent = message
            this.errorMessageTarget.style.display = 'block'
        }
    }

    _showSuccess(message) {
        if (this.hasSuccessMessageTarget) {
            this.successMessageTarget.textContent = message
            this.successMessageTarget.style.display = 'block'
        }
    }

    _clearMessages() {
        if (this.hasErrorMessageTarget) this.errorMessageTarget.style.display = 'none'
        if (this.hasSuccessMessageTarget) this.successMessageTarget.style.display = 'none'
    }

    _disableSubmit() {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true
            this.submitButtonTarget.textContent = 'Creating...'
        }
    }

    _enableSubmit() {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = false
            this.submitButtonTarget.textContent = 'Create Project'
        }
    }

}
